import BackwardIcon from '@assets/media/backward-60sec.svg';
import ForwardIcon from '@assets/media/forward-60sec.svg';
import PauseIcon from '@assets/media/pause-icon.svg';
import PlayIcon from '@assets/media/play-icon.svg';
import RestartIcon from '@assets/media/restart-icon.svg';
import {
  FocusContext,
  getCurrentFocusKey,
  setFocus,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import { playVideoVod, Translation, translationPacket } from 'common/actions';
import { Button, SimpleButton } from 'common/components/button';
import Icon from 'common/components/Icon';
import { ENABLE_BEGINING_SECONDS, ENABLE_LIVE_SECONDS } from 'common/config/constants';
import variables from 'common/config/variables';
import { Epg } from 'common/constants/data-types';
import { IVideoPlayer, PlayerControlsState } from 'common/reducers/videoPlayer';
import { getLivePoint } from 'common/services/helpers';
import { getClassName } from 'common/utils';
import AnimationDurations from 'common/utils/AnimationDurations';
import { usePrevious } from 'common/utils/hooks/usePrevious';
import { debounce, DebouncedFunc, isEmpty } from 'lodash';
import useCurrentTime from 'Player/hooks/useCurrentTime';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { PlayerControlsProps } from './Controls';
import EpgShow from './EpgShow';
const { DEFAULT_SEEK_STEP } = variables;

interface SeekButtonsStripeProps {
  // Redux injected
  i18n: typeof translationPacket;
  currentEpgStart: number;
  playerControlsState: PlayerControlsState;
  paused?: boolean;
  isFullscreen?: boolean;
  vod?: boolean;
  visible?: boolean;
  epgs?: IVideoPlayer['epgs'];
  isLiveEpg?: number;
  playVideoVod: (currentepg: Epg, options?: any) => void;
  //
  onFocus?: () => void;
  onBlur?: () => void;
  togglePlay: PlayerControlsProps['togglePlay'];
  forward: PlayerControlsProps['forward'];
  backward: PlayerControlsProps['backward'];
  setVideoToLive: PlayerControlsProps['setVideoToLive'];
  setVideoToBegining: PlayerControlsProps['setVideoToBegining'];
  toggleControls: PlayerControlsProps['toggleControls'];
  getDuration: PlayerControlsProps['getDuration'];
}
function SeekButtonsStripe(props: SeekButtonsStripeProps) {
  const playToggleButtonStyle = useMemo(
    () => (props.paused ? { paddingLeft: 6 } : undefined),
    [props.paused]
  );
  const previousPlayerControls = usePrevious(props.playerControlsState);
  const expanded = useMemo(
    () =>
      (['expanded', 'hidden'].includes(props.playerControlsState) &&
        previousPlayerControls !== 'mini') ||
      props.playerControlsState === 'expanded',
    [props.playerControlsState, previousPlayerControls]
  );
  const { ref, focusKey } = useFocusable({
    focusable: props.visible,
    preferredChildFocusKey: 'TOGGLE_PLAY',
    focusKey: 'SEEK_BUTTONS',
    saveLastFocusedChild: true,
    autoRestoreFocus: true,
    onFocus: props.onFocus,
    onBlur: props.onBlur
  });
  const { currentTime } = useCurrentTime(expanded);
  const debouncedPlayVideo = useRef<DebouncedFunc<VoidFunction> | undefined>(undefined);

  useEffect(() => {
    if (props.visible && props.isFullscreen) {
      setFocus('TOGGLE_PLAY');
    }
  }, [props.visible, props.isFullscreen]);

  const backward = useCallback(() => {
    AnimationDurations.set(
      'PLAYER_AUTOHIDE_TIMER',
      variables.PLAYER_MINIVIEW_EXTENDED_AUTOHIDE_TIMER
    );
    props.toggleControls();
    props.backward(DEFAULT_SEEK_STEP);
  }, [props.backward]);

  const forward = useCallback(() => {
    AnimationDurations.set(
      'PLAYER_AUTOHIDE_TIMER',
      variables.PLAYER_MINIVIEW_EXTENDED_AUTOHIDE_TIMER
    );
    props.toggleControls();
    props.forward(DEFAULT_SEEK_STEP);
  }, [props.forward]);

  const togglePlay = useCallback(() => {
    AnimationDurations.set('PLAYER_AUTOHIDE_TIMER', variables.PLAYER_MINIVIEW_AUTOHIDE_TIMER);
    props.toggleControls();
    props.togglePlay();
  }, [props.togglePlay]);

  const setVideoToLive = useCallback(() => {
    AnimationDurations.set('PLAYER_AUTOHIDE_TIMER', variables.PLAYER_MINIVIEW_AUTOHIDE_TIMER);
    props.toggleControls();
    props.setVideoToLive();
    setFocus('TOGGLE_PLAY');
  }, [props.setVideoToLive]);

  const setVideoToBegining = useCallback(() => {
    AnimationDurations.set('PLAYER_AUTOHIDE_TIMER', variables.PLAYER_MINIVIEW_AUTOHIDE_TIMER);
    props.toggleControls();
    props.setVideoToBegining();
    setFocus('TOGGLE_PLAY');
  }, [props.setVideoToBegining]);

  const nextEpg = useMemo(() => props.epgs?.next, [props.epgs?.next]);

  const playNext = useCallback(() => {
    if (isEmpty(nextEpg) || !nextEpg) {
      return;
    }
    props.toggleControls();
    debouncedPlayVideo.current && debouncedPlayVideo.current.cancel();
    debouncedPlayVideo.current = debounce(() => {
      AnimationDurations.set(
        'PLAYER_AUTOHIDE_TIMER',
        variables.PLAYER_MINIVIEW_EXTENDED_AUTOHIDE_TIMER
      );
      props.playVideoVod(nextEpg, { isFullscreen: true });
    }, variables.LONG_THROTTLE_TIMEOUT);
    debouncedPlayVideo.current();
  }, [nextEpg]);

  const livePointDisabled = useMemo(() => {
    if (props.isLiveEpg) {
      const delta = props.getDuration() - currentTime;
      return delta < ENABLE_LIVE_SECONDS;
    } else {
      return false;
    }
  }, [currentTime, props.currentEpgStart, props.isLiveEpg]);

  const beginDisabled = useMemo(() => currentTime < ENABLE_BEGINING_SECONDS, [currentTime]);

  const forwardDisabled = useMemo(() => {
    /**
     * Disable the forward button if seeking forward would go past the live point (for live)
     */
    const delta = props.isLiveEpg
      ? props.getDuration() - currentTime
      : getLivePoint() - (props.currentEpgStart + currentTime);
    const disabled = delta < DEFAULT_SEEK_STEP;
    if (disabled && getCurrentFocusKey() === 'FORWARD') {
      setFocus('TOGGLE_PLAY');
    }
    return disabled;
  }, [currentTime, props.currentEpgStart, props.isLiveEpg]);

  const rewindDisabled = useMemo(() => {
    const delta = currentTime - DEFAULT_SEEK_STEP;
    const disabled = delta <= 0 && props.vod;
    if (disabled && getCurrentFocusKey() === 'BACKWARD') {
      setFocus('TOGGLE_PLAY');
    }
    return disabled;
  }, [currentTime, props.currentEpgStart, props.vod]);

  const liveIcon = useMemo(
    () => <span className={getClassName('live-icon', { live: livePointDisabled })} />,
    [livePointDisabled]
  );

  const nextIcon = useMemo(
    () => <Icon className="next-icon" src={RestartIcon} size="verySmall" />,
    []
  );

  if (isEmpty(props.i18n)) {
    return null;
  }
  return (
    <FocusContext.Provider value={focusKey}>
      <div
        className={getClassName('seek-button-stripe', { visible: props.visible, expanded })}
        ref={ref}
      >
        <EpgShow type="prev" togglePlay={props.togglePlay} toggleControls={props.toggleControls} />
        <Button
          title={props.i18n.common.home.toUpperCase()}
          icon={RestartIcon}
          disabled={beginDisabled}
          onEnterPress={setVideoToBegining}
          testId="player-beginning-button"
        />
        <SimpleButton
          disabled={rewindDisabled}
          focusKey="BACKWARD"
          onEnterPress={backward}
          testId="player-backward-button"
        >
          <Icon src={BackwardIcon} size="medium" />
        </SimpleButton>
        <SimpleButton
          focusKey="TOGGLE_PLAY"
          onEnterPress={togglePlay}
          testId="player-toggle-play-button"
        >
          <Icon
            src={props.paused ? PlayIcon : PauseIcon}
            size="medium"
            style={playToggleButtonStyle}
          />
        </SimpleButton>
        <SimpleButton
          disabled={forwardDisabled}
          focusKey="FORWARD"
          onEnterPress={forward}
          testId="player-forward-button"
        >
          <Icon src={ForwardIcon} size="medium" />
        </SimpleButton>
        {!props.vod && (
          <Button
            title={props.i18n.common.live.toUpperCase()}
            icon={liveIcon}
            disabled={livePointDisabled}
            onEnterPress={setVideoToLive}
            testId="player-live-button"
          />
        )}
        {props.vod && (
          <Button
            title={props.i18n.common.upcoming.toUpperCase()}
            rightIcon={nextIcon}
            disabled={isEmpty(nextEpg)}
            onEnterPress={playNext}
            testId="player-next-button"
          />
        )}
        <EpgShow type="next" togglePlay={props.togglePlay} toggleControls={props.toggleControls} />
      </div>
    </FocusContext.Provider>
  );
}

const mapStateToProps = ({
  videoPlayer,
  i18n
}: {
  videoPlayer: IVideoPlayer;
  i18n: Translation;
}) => {
  return {
    i18n,
    paused: videoPlayer.video.paused,
    playerControlsState: videoPlayer.playerControlsState,
    currentEpgStart: isEmpty(videoPlayer.epgs) ? 0 : videoPlayer.epgs.current.start,
    isFullscreen: videoPlayer.video.isFullscreen,
    vod: videoPlayer.video?.vod,
    epgs: videoPlayer.epgs,
    isLiveEpg: videoPlayer.video?.isLiveEpg
  };
};
export default React.memo(
  connect(mapStateToProps, {
    playVideoVod
  })(SeekButtonsStripe)
);
